import { TableCell, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Plus, X } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { GHOST_FIELD_CLASSES } from "../cells/GhostInput";
import {
  QuoteItemLine,
  HydratedQuoteItemLine,
  CostingMethodEnum,
  isMultiCurrency,
  type Item,
  type CurrencyCode,
  formatPricingUnit,
} from "@forgepoint/shared";
import { getConversionTooltipText } from "@/modules/priceLists/pricingUnitUtils";
import { QuantityCell } from "../cells/QuantityCell";
import { CurrencyCell } from "../cells/CurrencyCell";
import { LineTotalCell } from "../cells/LineTotalCell";
import { ConvertedUnitPriceTooltip } from "../cells/ConvertedUnitPriceTooltip";
import { LineMarginCell } from "../cells/LineMarginCell";
import {
  displayedUnitPrice,
  type QuoteCurrencyFields,
} from "@/lib/quoteCurrency";
import {
  calculateLineTotal,
  lineMargin,
  lineUnitWeight,
  pricePerWeightUnit,
} from "../../utils";
import { UnitPriceSubline } from "./UnitPriceSubline";
import { LineWeightSubline } from "./LineWeightSubline";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useFeatureFlag } from "@/store/featureFlagStore";

interface ItemQuantityRowProps {
  line: QuoteItemLine;
  selectedItem: Item | null;
  hydratedLine?: HydratedQuoteItemLine;
  quantityIndex: number;
  readOnly: boolean;
  isLast: boolean;
  isLastInGroup: boolean; // Last quantity row before next item - add divider
  canRemove: boolean;
  currencyCode?: CurrencyCode;
  currencyContext?: QuoteCurrencyFields;
  currencyContextDirty?: boolean;
  onQuantityChange: (index: number, value: string) => void;
  onAddQuantity?: () => void;
  onRemoveQuantity: (index: number) => void;
  onSetOverride: (qtyIndex: number, currentPrice: string) => void;
  onOverrideChange: (qtyIndex: number, value: string) => void;
  onClearOverride: (qtyIndex: number) => void;
}

/**
 * Extra quantity break row of an item line, showing add-quantity on the last row only when a handler is given.
 * @param props - The line, the quantity break index, display context, and change handlers
 * @returns The quantity break row
 */
export function ItemQuantityRow({
  line,
  selectedItem,
  hydratedLine,
  quantityIndex,
  readOnly,
  isLast,
  isLastInGroup,
  canRemove,
  currencyCode,
  currencyContext,
  currencyContextDirty,
  onQuantityChange,
  onAddQuantity,
  onRemoveQuantity,
  onSetOverride,
  onOverrideChange,
  onClearOverride,
}: ItemQuantityRowProps) {
  const showMargin = useFeatureFlag("quote-line-margin");
  const showTotalWeight = useQuoteSettingsStore(
    (state) => state.settings?.showTotalWeight ?? false,
  );
  // Check if hydrated data matches current item selection
  const isHydratedDataValid = hydratedLine?.itemId === line.itemId;
  // effectiveItem carries the line's item overrides; the catalog item does not.
  const effectiveItem =
    (isHydratedDataValid
      ? (hydratedLine?.effectiveItem ?? hydratedLine?.item)
      : null) ?? selectedItem;
  const lineUnit = line.unitOverride ?? selectedItem?.unit ?? null;

  const quantityBreak = line.quantityBreaks[quantityIndex];
  const hydratedBreak = isHydratedDataValid
    ? hydratedLine?.quantityBreaks?.[quantityIndex]
    : undefined;
  const hasOverride = quantityBreak.unitPriceOverride !== undefined;
  const overrideValue = hasOverride
    ? quantityBreak.unitPriceOverride
    : undefined;
  // Since first quantity is now in parent row, this is "first" in the sub-rows
  const isFirstSubRow = quantityIndex === 1;

  const quantityChanged = quantityBreak.quantity !== hydratedBreak?.quantity;
  const shouldShowSkeleton = !hasOverride && quantityChanged;

  const multiCurrency = !!currencyContext && isMultiCurrency(currencyContext);
  // The override is entered and stored in the quote's display currency.
  const overrideCurrencyCode = multiCurrency
    ? (currencyContext?.quoteCurrency ?? currencyCode)
    : currencyCode;
  const lineTotal = calculateLineTotal(
    overrideValue,
    quantityBreak.quantity,
    hydratedBreak?.unitPrice,
    currencyContext,
  );
  // Prefer the backend-stamped display amount while the editor state still
  // matches the hydrated break; live edits fall back to shared-math derivation.
  // A dirty currency context means the stamped amount was computed under the
  // previous currency/snapshot, so it is stale and must be re-derived.
  const hydratedBreakFresh =
    !currencyContextDirty &&
    !quantityChanged &&
    (hydratedBreak?.unitPriceOverride ?? null) === (overrideValue ?? null);
  const freshConvertedAmount = hydratedBreakFresh
    ? (hydratedBreak?.convertedAmount ?? null)
    : null;

  const derivedUnitPrice = displayedUnitPrice(
    hydratedBreak,
    currencyContext,
    !!currencyContextDirty,
  );

  const conversionTooltipText = getConversionTooltipText(
    hydratedBreak,
    lineUnit,
  );

  const decimals = currencyContext?.quotePriceRounding?.decimals;
  const unitWeight = lineUnitWeight(hydratedBreak, effectiveItem, lineUnit);
  const perWeight = showTotalWeight
    ? pricePerWeightUnit(
        overrideValue ?? derivedUnitPrice,
        unitWeight,
        decimals,
      )
    : null;
  const perWeightSubline = perWeight ? (
    <UnitPriceSubline
      value={perWeight.value}
      unit={perWeight.unit}
      tooltip={`Unit price ÷ weight per unit (${unitWeight?.weight} ${formatPricingUnit(perWeight.unit)})`}
      currencyCode={overrideCurrencyCode}
      decimals={decimals}
    />
  ) : null;

  return (
    <TableRow
      className={cn(
        "group",
        isLastInGroup ? "border-b border-border" : "border-b-0",
      )}
    >
      {/* Empty drag handle cell */}
      <TableCell className="py-1 px-1 sm:px-2" />

      {/* Empty line number cell */}
      <TableCell className="py-1 px-0 sm:px-1" />

      {/* L-connector visual guide */}
      <TableCell className="py-1 px-1 sm:px-2">
        <div className="flex items-center h-full pl-1 sm:pl-2">
          <div
            className={cn(
              "w-3 sm:w-4 border-l border-muted-foreground/30",
              isFirstSubRow ? "h-full border-t rounded-bl" : "h-full",
              isLast && "h-1/2 self-start",
            )}
          />
          <div className="w-2 sm:w-3 border-b border-muted-foreground/30" />
        </div>
      </TableCell>

      {/* Quantity Input */}
      <TableCell className="text-right py-1 px-1 sm:px-2">
        <div className="flex flex-col items-end gap-0.5">
          <QuantityCell
            value={quantityBreak.quantity}
            onChange={(v) => onQuantityChange(quantityIndex, v)}
            disabled={readOnly}
          />
          {showTotalWeight && (
            <LineWeightSubline
              weight={hydratedBreak?.totalWeight}
              unit={hydratedBreak?.totalWeightUnit ?? effectiveItem?.weightUnit}
            />
          )}
        </div>
      </TableCell>

      {/* Unit (empty for quantity sub-rows) */}
      <TableCell className="py-1 px-1 sm:px-2" />

      {/* Margin */}
      {showMargin && (
        <TableCell className="text-right py-1 px-1 sm:px-2">
          <LineMarginCell
            margin={lineMargin(
              overrideValue,
              hydratedBreak?.unitPrice,
              hydratedBreak?.unitCost,
              currencyContext,
            )}
            quantity={hydratedBreak?.quantity}
            baseCurrencyCode={currencyCode}
            isBom={effectiveItem?.costingMethod === CostingMethodEnum.BOM}
            shouldShowSkeleton={shouldShowSkeleton}
          />
        </TableCell>
      )}

      {/* Unit Price (with override if present) */}
      <TableCell className="text-right py-1 px-0.5 sm:px-2">
        <div className="flex flex-col items-end gap-0.5">
          {hasOverride ? (
            <>
              <div className="flex w-full items-center justify-end gap-0.5">
                <CurrencyCell
                  value={overrideValue ?? null}
                  onChange={(v) => onOverrideChange(quantityIndex, v)}
                  disabled={readOnly}
                  editable={!readOnly}
                  isUnitPrice
                  currencyCode={overrideCurrencyCode}
                  decimals={decimals}
                />
                {!readOnly && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-5 w-5 shrink-0 sm:h-7 sm:w-7"
                    onClick={() => onClearOverride(quantityIndex)}
                  >
                    <X className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                  </Button>
                )}
              </div>
              {hydratedBreak?.unitPrice && (
                <ConvertedUnitPriceTooltip
                  baseUnitPrice={hydratedBreak.unitPrice}
                  baseCurrencyCode={currencyCode}
                  currencyContext={currencyContext}
                  className="text-[10px] sm:text-xs tabular-nums line-through text-muted-foreground"
                />
              )}
              {perWeightSubline}
            </>
          ) : shouldShowSkeleton ? (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex justify-end">
                    <Skeleton className="h-4 w-10 sm:h-5 sm:w-16" />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Save your updates to recalculate prices</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : hydratedBreak?.unitPrice ? (
            <div className="flex w-full items-center justify-end gap-0.5">
              <div className="flex flex-col items-end">
                {readOnly ? (
                  <ConvertedUnitPriceTooltip
                    baseUnitPrice={hydratedBreak.unitPrice}
                    baseCurrencyCode={currencyCode}
                    currencyContext={currencyContext}
                    className="text-xs sm:text-sm tabular-nums"
                  />
                ) : (
                  <button
                    type="button"
                    aria-label="Override price"
                    onClick={() =>
                      onSetOverride(quantityIndex, derivedUnitPrice ?? "")
                    }
                    className={cn(
                      "-mx-0.5 sm:-mx-2 flex h-6 sm:h-8 items-center rounded-md px-0.5 sm:px-2 py-0.5 sm:py-1 transition-colors",
                      GHOST_FIELD_CLASSES,
                    )}
                  >
                    <ConvertedUnitPriceTooltip
                      baseUnitPrice={hydratedBreak.unitPrice}
                      baseCurrencyCode={currencyCode}
                      currencyContext={currencyContext}
                      className="text-xs sm:text-sm tabular-nums"
                    />
                  </button>
                )}
                {hydratedBreak.convertedFromUnitPrice &&
                hydratedBreak.pricingUnit ? (
                  <UnitPriceSubline
                    value={hydratedBreak.convertedFromUnitPrice}
                    unit={hydratedBreak.pricingUnit}
                    tooltip={conversionTooltipText}
                    currencyCode={currencyCode}
                    decimals={decimals}
                  />
                ) : (
                  perWeightSubline
                )}
              </div>
            </div>
          ) : (
            <span className="text-xs sm:text-sm text-muted-foreground">-</span>
          )}
        </div>
      </TableCell>

      {/* Line Total */}
      <TableCell className="text-right py-1 px-1 sm:px-2">
        <LineTotalCell
          lineTotal={lineTotal}
          unitPrice={hydratedBreak?.unitPrice ?? null}
          quantity={quantityBreak.quantity}
          overrideUnitPrice={overrideValue ?? null}
          convertedAmount={freshConvertedAmount}
          shouldShowSkeleton={shouldShowSkeleton}
          currencyCode={currencyCode}
          currencyContext={currencyContext}
        />
      </TableCell>

      {/* Actions - Delete and Add quantity buttons on last row */}
      <TableCell className="py-1 px-1 sm:px-2">
        {isLast && !readOnly && (
          <div className="flex items-center gap-0.5 sm:gap-1">
            {canRemove && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8 touch-manipulation"
                      onClick={() => onRemoveQuantity(quantityIndex)}
                    >
                      <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Remove this quantity option</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {onAddQuantity && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8 touch-manipulation"
                      onClick={onAddQuantity}
                    >
                      <Plus className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Add another quantity option</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
